'use es6';
/*
 * Adds line highlighting to the CodeMirror editor.
 * Adds the following props:
 *   - highlightLines: PropTypes.arrayOf(PropTypes.shape({
 *      from: PropTypes.uint32.isRequired,
 *      to: PropTypes.uint32,
 *    })),
 *
 * Highlighted lines will have the `.line-highlight` class added to their line and gutter.
 *
 * A highlight class def can be imported via
 *   react-codemirror/static-1.5611/sass/highlightLines
 */

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import PropTypes from 'prop-types';
import getComponentName from 'react-utils/getComponentName';
import LineRange from 'react-codemirror/model/LineRange';
import { loadReactCodeMirrorCSS } from 'react-codemirror/util/LoadCSS';
import loadCSS from 'react-codemirror/decorator/_internal/loadCSS';
import { compose } from '../util/miniUnderscore.js';
const withLoadCSS = loadCSS([loadReactCodeMirrorCSS('/sass/highlightLines.css')]);
const HIGHLIGHT_CLASS = 'line-highlight'; // TODO: Move this to a util.
// IE does not support new Set([iter])

const initSet = (() => {
  const testSet = new Set([1]);
  const isIteratorConstructorSupported = !!testSet.size;
  return iter => {
    if (!iter) {
      return new Set();
    }

    if (isIteratorConstructorSupported) {
      return new Set(iter);
    }

    const s = new Set();
    iter.forEach(s.add, s);
    return s;
  };
})();
/**
 * @return {Function}
 */


export default function highlightLines() {
  return function withHighlightLines(CodemirrorComponent) {
    /**
     * @class
     */
    class WithHighlightLines extends Component {
      constructor(props) {
        super(props);
        this.codemirror = null;
        this.highlightedLines = [];
        this.firstHighlightedLine = null;
        this.refCodemirror = this.refCodemirror.bind(this);
      }

      componentDidUpdate(prevProps) {
        if (prevProps.highlightLines !== this.props.highlightLines) {
          this.updateLineHighlights();
        } else if (prevProps.scrollHighlightIntoView !== this.props.scrollHighlightIntoView) {
          this.scrollIntoView();
        }
      }

      codemirrorMounted(codemirror) {
        if (this.codemirror === codemirror) {
          return;
        }

        this.codemirror = codemirror;

        if (!this.codemirror) {
          return;
        }

        this.updateLineHighlights();
      }

      updateLineHighlights() {
        if (!this.codemirror) {
          return;
        }

        const {
          highlightLines: ranges
        } = this.props;
        this.codemirror.operation(() => {
          if (!(ranges && ranges.length)) {
            // Extend line range to clear all highlights after user adds new line to code
            const lastHighlightedLine = this.highlightedLines.slice(-1).pop();
            const extendLineRange = lastHighlightedLine ? lastHighlightedLine + 1 : 0;
            this.highlightedLines.push(extendLineRange);
            this.clearHighlights(this.highlightedLines);
            this.highlightedLines = [];
            this.firstHighlightedLine = null;
            return;
          }

          const linesToRemoveHighlight = initSet(this.highlightedLines);
          const newHighlightedLines = [];
          let lowest = null;
          ranges.forEach(({
            from,
            to
          }) => {
            to = to && to > from ? to : from;

            for (let lineNumber = from - 1; lineNumber < to; ++lineNumber) {
              newHighlightedLines.push(lineNumber);

              if (lowest === null) {
                lowest = lineNumber;
              } else if (lowest > lineNumber) {
                lowest = lineNumber;
              }

              if (linesToRemoveHighlight.has(lineNumber)) {
                linesToRemoveHighlight.delete(lineNumber);
              } else {
                this.highlightLine(lineNumber);
              }
            }
          });
          this.clearHighlights(linesToRemoveHighlight);
          this.highlightedLines = newHighlightedLines;
          this.firstHighlightedLine = lowest;
          this.scrollIntoView();
        });
      }

      highlightLine(lineNumber, highlight = true) {
        const classMethod = highlight ? 'addLineClass' : 'removeLineClass';
        this.codemirror.doc[classMethod](lineNumber, 'gutter', HIGHLIGHT_CLASS);
        this.codemirror.doc[classMethod](lineNumber, 'background', HIGHLIGHT_CLASS);
      }

      clearHighlight(lineNumber) {
        this.highlightLine(lineNumber, false);
      }

      clearHighlights(highlights) {
        highlights.forEach(this.clearHighlight, this);
      }

      scrollIntoView() {
        if (!this.codemirror) {
          return;
        }

        if (this.props.scrollHighlightIntoView && this.firstHighlightedLine != null) {
          const line = this.firstHighlightedLine; // Let codemirror finish loading before trying to scroll

          setTimeout(() => {
            this.codemirror.scrollIntoView({
              line,
              ch: 0
            }, 40);
          }, 0);
        }
      }

      refCodemirror(ref) {
        const codemirror = ref && ref.codemirror;
        this.codemirrorMounted(codemirror);

        if (this.props.refCodemirror) {
          this.props.refCodemirror(ref);
        }
      }

      render() {
        const _this$props = this.props,
              props = _objectWithoutPropertiesLoose(_this$props, ["highlightLines", "scrollHighlightIntoView"]);

        return /*#__PURE__*/_jsx(CodemirrorComponent, Object.assign({}, props, {
          refCodemirror: this.refCodemirror
        }));
      }

    }
    /*
     * Statics
     */


    WithHighlightLines.propTypes = {
      /** @type {Array<LineRange>} Array of line ranges to highlight */
      highlightLines: LineRange.lineRangesPropType,

      /** @type {Boolean} Scroll to first highlighted lines */
      scrollHighlightIntoView: PropTypes.bool,

      /** @type {Function} */
      refCodemirror: PropTypes.func
    };
    WithHighlightLines.displayName = `WithHighlightLines(${getComponentName(CodemirrorComponent)})`;
    return compose(withLoadCSS)(WithHighlightLines);
  };
}